import { useContext, useState, useRef, useEffect } from "react";
import NavMenuItem from "../ui/nav-menu-item";
import CreateNewTable from "./create-new-table";
import TableContext from "../../contexts/table-context";
import BusinessContext from "../../contexts/business-context";
import { SidebarShimmer } from "../ui/shimmers";
import NavSection from "./nav-section";
import UserContext from "../../contexts/user-context";
import DropdownOptionItem from "../ui/dropdown-option-item";
import DeleteTableModal from "../ui/delete-table-modal";
import SideBarContext from "../../contexts/side-bar-context";
import { deleteTable, updateTable } from "../../lib/table";
import { useRouter } from "next/router";
import CreateNewFolder from "../ui/create-new-folder";
import SecondaryDropdownContainer from "../ui/secondary-dropdown-container";
import PrimarySearch from "../ui/primary-search";
import EmptyState from "../ui/empty-state";
import { useTranslations } from "next-intl";

const TableNav = () => {
    const t = useTranslations('table');
    const { tables, fetchTables, duplicateTable } = useContext(TableContext);
    const { setIsTableDeleteModalOpen } = useContext(SideBarContext);
    const userCtx = useContext(UserContext);
    const user = userCtx.user;
    const { activeBusiness, currentUser, setNotificationContent, isGuestUser } = useContext(BusinessContext);

    const router = useRouter();
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(null);
    const [openedOption, setOpenedOption] = useState(null);
    const [isCreateFolderActive, setIsCreateFolderActive] = useState(false);
    const toggleCreateFolder = (sectionName) => {
        setIsCreateFolderActive(!isCreateFolderActive);
        if (!folderType || !isCreateFolderActive) {
            setFolderType(sectionName);
        }
        else {
            setFolderType(null);
        }
    };
    const [folderType, setFolderType] = useState(null);
    const closeOption = () => {
        setOpenedOption(null);
    }
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteModal, setDeleteModal] = useState(null);
    const actualTables = tables?.filter(table => !table?.metadata?.extra?.folderData?.is_folder) || [];
    const [tableFolders, setTableFolders] = useState(tables?.filter(table => table?.metadata?.extra?.folderData?.is_folder) || []);
    const folderData = tableFolders?.map(folder => folder?.metadata?.extra?.folderData).filter(data => data);
    const sortedTables = actualTables ? [...actualTables].sort((a, b) => {
        return new Date(b?.created_at) - new Date(a?.created_at);
    }) : [];
    const [openedFolders, setOpenedFolders] = useState([]);
    const [draggedTable, setDraggedTable] = useState(null);
    const [dragOverFolder, setDragOverFolder] = useState(null);
    const [dragOverMainArea, setDragOverMainArea] = useState(false);
    const [moveToFolder, setMoveToFolder] = useState(false);

    const toggleDeleteModal = () => {
        setDeleteModalOpen(!deleteModalOpen);
        setIsTableDeleteModalOpen(!deleteModalOpen);
    }

    useEffect(() => {
        setTableFolders(tables?.filter(table => table?.metadata?.extra?.folderData?.is_folder) || []);
    }, [tables]);

    const existingFolderIds = folderData.map(folder => folder.id);

    const extractFolderId = (folder) => {
        if (folder?.metadata.extra?.folder_id && existingFolderIds.includes(folder.metadata.extra.folder_id)) {
            return folder.metadata?.extra.folder_id
        }
        else if (folder?.metadata.extra?.folderData) {
            if (Array.isArray(folder?.metadata.extra.folderData)) {
                const userFolder = folder?.metadata.extra.folderData.find(fd => fd.user === currentUser?.id && existingFolderIds.includes(fd.id));
                return userFolder ? userFolder.id : undefined;
            } else if (folder?.metadata.extra.folderData?.user === currentUser?.id && existingFolderIds.includes(folder.metadata.extra.folderData.id)) {
                return folder.metadata.extra.folderData.id;
            }
            return;
        }
    }

    const handleChangeName = (tableId, newName) => {
        const table = tables.find(t => (t.uuid === tableId));
        if (!table || newName === table.name) {
            setIsEditing(null);
            setOpenedOption(null);
            return;
        }

        let name;
        if (newName === '') {
            name = '‎';
        } else {
            name = newName;
        }

        const newTableData = { name: name };
        setIsSaving(true);
        const callback = (json) => {
            fetchTables();
            setIsEditing(null);
            setOpenedOption(null);
            setIsSaving(false);
        }
        const onError = (json) => {
            console.error(json);
            setIsSaving(false);
            setIsEditing(null);
            setOpenedOption(null);
        }

        updateTable(tableId, newTableData, callback, activeBusiness, onError, router);
    }

    const handleDragStart = (e, table, sectionName) => {
        setDraggedTable({ ...table, draggedFromSection: sectionName });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', table.uuid);
    }

    const handleDragEnd = () => {
        setDraggedTable(null);
        setDragOverFolder(null);
        setDragOverMainArea(false);
    }

    const handleDragOver = (e, folderId, sectionName) => {
        e.preventDefault();
        e.stopPropagation();

        // Only allow drag if from same section
        if (!draggedTable || draggedTable.draggedFromSection !== sectionName) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }

        e.dataTransfer.dropEffect = 'move';
        setDragOverFolder(folderId);
    }

    const handleDragLeave = (e, folderId) => {
        // Only clear drag over if we're actually leaving the folder area
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverFolder(null);
        }
    }

    const handleDrop = (e, table, folderId, sectionName) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling to main area
        setDragOverFolder(null);

        if (!draggedTable) return;

        // Prevent moving tables across sections
        if (draggedTable.draggedFromSection !== sectionName) {
            setDraggedTable(null);
            return;
        }

        // Don't move table if it's already in this folder
        if (draggedTable.metadata?.extra?.folder_id === folderId) {
            setDraggedTable(null);
            return;
        }

        moveTableToFolder(draggedTable, folderId);
    }

    const handleMainAreaDragOver = (e, sectionName) => {
        e.preventDefault();

        // Only allow drag if from same section
        if (!draggedTable || draggedTable.draggedFromSection !== sectionName) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }

        e.dataTransfer.dropEffect = 'move';
        setDragOverMainArea(sectionName); // Store which section
    }

    const handleMainAreaDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverMainArea(false);
        }
    }

    const handleMainAreaDrop = (e) => {
        e.preventDefault();
        setDragOverMainArea(false);

        if (!draggedTable) return;

        // Check both legacy folder_id and new folderData array
        const hasFolderId = draggedTable?.metadata?.extra?.folder_id;
        const hasFolderData = Array.isArray(draggedTable?.metadata?.extra?.folderData) &&
            draggedTable.metadata.extra.folderData.some(fd => fd.user === currentUser.id);

        if (!hasFolderId && !hasFolderData) {
            setDraggedTable(null);
            return;
        }

        // Only remove from folder if actually dropping in main area (not in a folder)
        if (e.target.closest('[data-folder-drop]')) {
            return; // Don't remove from folder if dropping on a folder
        }

        // Remove table from folder
        handleRemoveFromFolder(draggedTable);
        setDraggedTable(null);
    }

    const moveTableToFolder = (table, folderId) => {
        const currentFolder = folderData.find(folder => folder.id === folderId);

        // Update table with new folder_id
        const updatedMetadata = {
            ...table.metadata,
            extra: {
                ...table.metadata.extra,
                folder_id: undefined,
                folderData: [
                    ...(Array.isArray(table.metadata.extra?.folderData)
                        ? table.metadata.extra.folderData.filter(fd => fd.user !== currentUser.id)
                        : []),
                    { id: folderId, user: currentUser.id }
                ]
            }
        };

        const newTableData = { metadata: updatedMetadata };
        setIsSaving(true);

        const callback = (json) => {
            fetchTables();
            setIsSaving(false);
            setNotificationContent({
                showNotification: true,
                header: 'Move successful',
                message: <div>Table has been moved to <strong>{currentFolder.folder_name}</strong>.</div>,
                icon: 'check',
            });
            setTimeout(() => {
                setNotificationContent(prev => ({ ...prev, showNotification: false }));
            }, 5000);
        }

        const onError = (json) => {
            console.error('Error moving table to folder:', json);
            setIsSaving(false);
        }

        updateTable(table.uuid, newTableData, callback, activeBusiness, onError, router);
    }

    const handleRemoveFromFolder = (table) => {
        // Remove table from folder by removing folder_id
        const updatedMetadata = {
            ...table.metadata,
            extra: {
                ...table.metadata.extra,
                folder_id: undefined,
                folderData: Array.isArray(table.metadata.extra.folderData)
                    ? table.metadata.extra.folderData.filter(fd => fd.user !== currentUser.id)
                    : undefined
            }
        };

        const newTableData = { metadata: updatedMetadata };
        setIsSaving(true);

        const callback = (json) => {
            fetchTables();
            setIsSaving(false);
            setNotificationContent({
                showNotification: true,
                header: 'Move successful',
                message: <div>Table has been moved out of folder.</div>,
                icon: 'check',
            });
            setTimeout(() => {
                setNotificationContent(prev => ({ ...prev, showNotification: false }));
            }, 5000);
        }

        const onError = (json) => {
            console.error('Error removing table from folder:', json);
            setIsSaving(false);
        }

        updateTable(table.uuid, newTableData, callback, activeBusiness, onError, router);
    }

    if (tables === null || isGuestUser === undefined) {
        return (
            <NavSection>
                <div className="grid gap-[4px] content-start relative ">
                    <SidebarShimmer />
                </div>
            </NavSection>
        );
    }

    const privateTables = sortedTables.filter(table =>
        table?.permission?.charAt(1) === '0' && table.owner?.user.email === user?.email
    );

    const teamTables = sortedTables?.filter(table => table?.permission?.charAt(1) !== '0');

    const sharedTables = sortedTables.filter(table =>
        table?.permission?.charAt(1) === '0' && table.owner?.user.email !== user?.email
    );

    const handleDeleteTable = (tableID) => {
        const callback = () => {
            setOpenedOption(null);
            setDeleteModalOpen(false);
            setDeleteModal(null);
            fetchTables();
            localStorage.removeItem(`table-${tableID}-local-rows`);
            if (router.asPath.includes(`t/${tableID}`)) {
                router.push(`/${activeBusiness.uuid}/home`);
            }
        };
        const onError = (json) => {
            console.error(json);
        }

        deleteTable(tableID, callback, activeBusiness, onError, router);
    }


    const handleRenameFolder = (tableId, newName) => {
        setIsSaving(true);
        const table = tables.find(t => (t.uuid === tableId));
        const updatedMetadata = {
            ...table.metadata,
            extra: {
                ...table.metadata.extra,
                folderData: {
                    ...table.metadata.extra.folderData,
                    folder_name: newName
                }
            }
        };

        const newTableData = {
            ...table,
            metadata: updatedMetadata
        };

        if (newName === '' || folderData.some(folder => folder.folder_name === newName && folder.id !== table.metadata.extra.folderData.id)) {
            const isEmpty = newName === '';
            setNotificationContent({
                showNotification: true,
                header: isEmpty ? 'Folder name is required' : 'Folder name already exists',
                message: isEmpty
                    ? 'Please enter a valid folder name.'
                    : <div>Folder name <strong>{newName}</strong> already exists, please choose a different name.</div>,
                icon: 'circle-alert',
            });
            setTimeout(() => {
                setNotificationContent(prev => ({ ...prev, showNotification: false }));
            }, 5000);
            setIsSaving(false);
            return;
        }
        const callBack = (json) => {
            const updatedFolders = tableFolders.map(folder =>
                folder.uuid === json.uuid ? json : folder
            );
            setTableFolders(updatedFolders);
            setIsEditing(null);
            setIsSaving(false);
            closeOption();
        }
        const onError = (json) => {
            console.error('Error renaming folder:', json);
            setIsSaving(false);
        }

        if (newName && newName.trim() !== '') {
            updateTable(table.uuid, newTableData, callBack, activeBusiness, onError, router);
        }
    }

    const SectionDropdownContent = ({ closeDropdown, sectionName }) => {
        return (
            <>
                <DropdownOptionItem icon="folder-plus" additionalclasses="!py-[4px]" onClick={() => { setIsCreateFolderActive(true); setFolderType(sectionName); closeDropdown(); }}>Create new folder</DropdownOptionItem>
            </>
        )
    }

    const DropDownContent = ({ table, sectionName }) => {
        const currentFolderId = extractFolderId(table);
        const isInFolder = currentFolderId !== undefined && currentFolderId !== null;
        const isFolder = table?.metadata?.extra?.folderData?.is_folder;
        const availableFolders = folderData.filter(folder =>
            folder.id !== currentFolderId // Use currentFolderId instead
        );
        const privateFolders = availableFolders.filter(folder => folder.permission === 'private' || folder.permission === undefined);
        const sharedFolders = availableFolders.filter(folder => folder.permission === 'shared');
        const teamFolders = availableFolders.filter(folder => folder.permission === 'team');

        const sectionAvailableFolders = sectionName === 'Private' ? privateFolders : sectionName === 'Shared' ? sharedFolders : teamFolders;
        const moveToFolderButtonRef = useRef(null);

        const [filteredAvailableFolders, setFilteredAvailableFolders] = useState(sectionAvailableFolders);


        const onSearchFolders = (value) => {
            const filtered = sectionAvailableFolders.filter(folder =>
                folder.folder_name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredAvailableFolders(filtered);
        }

        return (
            <>
                <div onClick={(e) => { e.stopPropagation() }} className="grid gap-[4px] content-start">
                    <DropdownOptionItem icon="pen-line" additionalclasses="!py-[4px]" onClick={() => { setIsEditing(table?.uuid) }}>Rename</DropdownOptionItem>
                    {!isFolder && (
                        <>
                            <DropdownOptionItem icon="copy-plus" additionalclasses="!py-[4px]" onClick={() => { duplicateTable(table); setOpenedOption(null); }}>Duplicate</DropdownOptionItem>

                            <div className="relative">
                                <DropdownOptionItem
                                    ref={moveToFolderButtonRef}
                                    icon="folder-plus"
                                    additionalclasses={`!py-[4px]  ${availableFolders.length === 0 ? 'opacity-50' : ''}`}
                                    onClick={() => setMoveToFolder(!moveToFolder)}
                                    isDisabled={availableFolders.length === 0}
                                >
                                    Move to folder
                                </DropdownOptionItem>
                                <SecondaryDropdownContainer isOpen={moveToFolder} toggleDropdown={() => setMoveToFolder(!moveToFolder)} parentRef={moveToFolderButtonRef} additionalclasses="!min-w-[181px] !top-6 !left-[180px]">
                                    <PrimarySearch placeholder="Search folders" onSearch={onSearchFolders} additionalclasses="!w-[181px]" />
                                    {filteredAvailableFolders.length > 0 ? (
                                        filteredAvailableFolders.map(folder => (
                                            <DropdownOptionItem
                                                key={folder.id}
                                                icon="folder"
                                                additionalclasses="!py-[4px]"
                                                onClick={() => {
                                                    moveTableToFolder(table, folder.id);
                                                    setOpenedOption(null);
                                                    setMoveToFolder(false);
                                                }}
                                            >
                                                {folder.folder_name}
                                            </DropdownOptionItem>
                                        ))
                                    ) : (
                                        <div className="m-auto">
                                            <EmptyState>No folders found</EmptyState>
                                        </div>
                                    )}
                                </SecondaryDropdownContainer>
                            </div>
                        </>
                    )}

                    {isInFolder && (
                        <DropdownOptionItem icon="folder-x" additionalclasses="!py-[4px]" onClick={() => { handleRemoveFromFolder(table); setOpenedOption(null); }}>Remove from folder</DropdownOptionItem>
                    )}
                    <DropdownOptionItem icon="trash-2" additionalclasses="hover:custom-text-danger-500 !py-[4px]" onClick={(e) => { e.stopPropagation(); setDeleteModal(table?.uuid); setOpenedOption(null); toggleDeleteModal(); }}>Delete</DropdownOptionItem>
                </div>
            </>
        )
    }

    const renderTableItem = (table, hasToggle = false, isChild = false, sectionName = '') => {
        const tableId = table.uuid;
        const isDragging = draggedTable?.uuid === tableId;


        return (
            <div
                key={tableId}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, table, sectionName)} // Pass sectionName
                onDragEnd={handleDragEnd}
                className={`${isDragging ? 'opacity-50' : ''} cursor-move`}
            >
                <NavMenuItem
                    dropdownContent={<DropDownContent table={table} sectionName={sectionName} />}
                    toggle={hasToggle}
                    isSaving={isSaving}
                    toggleEditing={() => setIsEditing(null)}
                    handleChangeName={handleChangeName}
                    tableId={tableId}
                    isEditing={isEditing === tableId}
                    closeEllipsis={closeOption}
                    handleEllipsis={() => { setOpenedOption(tableId); setMoveToFolder(false); }}
                    isEllipsisOpen={openedOption === tableId}
                    chevron
                    icon={table?.metadata?.render_types?.[0]?.type === 'document' ? 'file' : 'table'}
                    href={`/t/${tableId}`}
                    isChild={isChild}
                >
                    {table.name}
                </NavMenuItem>
                <DeleteTableModal
                    handleDeleteTable={handleDeleteTable}
                    table={table}
                    deleteModalOpen={deleteModalOpen && (tableId === deleteModal)}
                    toggleDeleteModal={toggleDeleteModal}
                />
            </div>
        );
    };

    const renderTableSection = (tables, sectionName, showCreateNew = false, showAddFolder = true) => {
        // Get folders for this section
        const sectionFolders = sectionName === 'Private'
            ? tableFolders.filter(folder => folder.metadata?.extra?.folderData?.permission === 'private' || folder.metadata?.extra?.folderData?.permission === undefined || folder.metadata?.extra?.folderData?.permission === null)
            : sectionName === 'Shared'
            ? tableFolders.filter(folder => folder.metadata?.extra?.folderData?.permission === 'shared')
            : tableFolders.filter(folder => folder.metadata?.extra?.folderData?.permission === 'team');

        const sectionFolderIds = sectionFolders.map(folder => folder.metadata?.extra?.folderData?.id);

        // Filter tables that have a valid folder ID in THIS section
        const tablesWithValidFolders = tables.filter(table => {
            const folderId = extractFolderId(table);
            return folderId !== null && sectionFolderIds.includes(folderId);
        });

        // Tables without folders OR tables in folders from other sections (should be shown without folder)
        const tablesWithoutFolders = tables.filter(table => {
            // Get folderData array
            const folderDataArr = Array.isArray(table?.metadata?.extra?.folderData)
                ? table.metadata.extra.folderData
                : [];

            const hasFolder = folderDataArr.some(fd => existingFolderIds.includes(fd.id));
            const hasLegacyFolder = existingFolderIds.includes(table?.metadata?.extra?.folder_id);

            // If table has no folder data at all, include it
            if (!hasFolder && !hasLegacyFolder) {
                return true;
            }

            // If table has folder data but it's not in this section's folders, include it
            const folderId = extractFolderId(table);
            if (folderId && !sectionFolderIds.includes(folderId)) {
                return true;
            }

            return false;
        });

        // Group tables by folder ID
        const tablesByFolder = tablesWithValidFolders.reduce((acc, table) => {
            const folderId = extractFolderId(table);
            if (folderId) {
                if (!acc[folderId]) {
                    acc[folderId] = [];
                }
                acc[folderId].push(table);
            }
            return acc;
        }, {});
        const privateFolders = tableFolders.filter(folder => folder.metadata?.extra?.folderData?.permission === 'private' || folder.metadata?.extra?.folderData?.permission === undefined || folder.metadata?.extra?.folderData?.permission === null);
        const sharedFolders = tableFolders.filter(folder => folder.metadata?.extra?.folderData?.permission === 'shared');
        const teamFolders = tableFolders.filter(folder => folder.metadata?.extra?.folderData?.permission === 'team');

        const foldersToDisplay = sectionName === 'Private' ? privateFolders
            : sectionName === 'Shared' ? sharedFolders
                : teamFolders;

        if (foldersToDisplay.length === 0) {
            if (tables.length === 0 && sectionName !== 'Private') {
                return null;
            }
        }

        const localeName = sectionName === 'Private' ? t('private')
            : sectionName === 'Shared' ? t('shared')
                : t('team');

        return (
            <NavSection section={localeName} hasEllipsis={sectionName === 'Private' && tableFolders.length > 0} sectionDropdown={<SectionDropdownContent sectionName={sectionName} />}>
                <div
                    className="grid gap-[4px] content-start relative"
                    onDragOver={(e) => handleMainAreaDragOver(e, sectionName)}
                    onDragLeave={handleMainAreaDragLeave}
                    onDrop={handleMainAreaDrop}
                >
                    {!isGuestUser &&
                        <div className="grid gap-[4px] content-start relative">
                            {showCreateNew && <CreateNewTable />}
                            {showAddFolder && <CreateNewFolder folderNames={folderData.map(folder => folder.folder_name)} isCreateActive={isCreateFolderActive && folderType === sectionName} toggleCreate={() => { toggleCreateFolder(sectionName) }} folderPermission={sectionName.toLowerCase()} />}
                            {(showCreateNew || showAddFolder) && <div className="border-b-[0.5px] custom-border-grey-400 opacity-10 my-[4px]" />}
                        </div>
                    }

                    {foldersToDisplay.map(table => {
                        const tableId = table.uuid;
                        const folder = table.metadata.extra.folderData;
                        const isDropTarget = dragOverFolder === folder?.id;
                        const isDraggedOver = draggedTable && dragOverFolder === folder?.id;

                        return (
                            <>
                                <div
                                    key={folder?.id}
                                    data-folder-drop="true"
                                    onDragOver={(e) => handleDragOver(e, folder?.id, sectionName)}
                                    onDragLeave={(e) => handleDragLeave(e, folder?.id)}
                                    onDrop={(e) => handleDrop(e, table, folder?.id, sectionName)}
                                    className={`${isDraggedOver ? 'custom-bg-grey-200 bg-opacity-50 max-w-[214px] rounded-[8px]' : ''} transition-all duration-200 grid gap-[4px]`}
                                >
                                    <NavMenuItem
                                        dropdownContent={<DropDownContent table={table} sectionName={sectionName} />}
                                        // toggle={true}
                                        isSaving={isSaving}
                                        toggleEditing={() => setIsEditing(null)}
                                        handleChangeName={handleRenameFolder}
                                        tableId={tableId}
                                        isEditing={isEditing === tableId}
                                        closeEllipsis={closeOption}
                                        handleEllipsis={() => setOpenedOption(tableId)}
                                        isEllipsisOpen={openedOption === tableId}
                                        icon={openedFolders.includes(folder?.id) ? 'folder-open' : 'folder'}
                                        onClick={() => {
                                            if (openedFolders.includes(folder?.id)) {
                                                setOpenedFolders(openedFolders.filter(id => id !== folder?.id));
                                            } else {
                                                setOpenedFolders([...openedFolders, folder?.id]);
                                            }
                                        }}
                                        className={isDraggedOver ? 'custom-bg-grey-200 bg-opacity-50 w-full' : ''}
                                    >
                                        {folder?.folder_name}
                                    </NavMenuItem>

                                    {/* Display tables in this folder if folder is open */}
                                    {openedFolders.includes(folder?.id) && tablesByFolder[folder?.id] && (
                                        <div className="ml-[16px] grid gap-[4px] content-start mt-[4px]">
                                            {tablesByFolder[folder?.id].map((table) => renderTableItem(table, true, true, sectionName))}
                                        </div>
                                    )}
                                </div>
                                <DeleteTableModal
                                    handleDeleteTable={handleDeleteTable}
                                    table={table}
                                    deleteModalOpen={deleteModalOpen && (tableId === deleteModal)}
                                    toggleDeleteModal={toggleDeleteModal}
                                    folder={folder}
                                />
                            </>
                        );
                    })}

                    {(foldersToDisplay.length > 0) && <div className="border-b-[0.5px] custom-border-grey-400 opacity-10 my-[4px]" />}

                    {/* Display tables without folders */}
                    <div className={` ${dragOverMainArea === sectionName ? 'grid gap-[4px] custom-bg-grey-200 bg-opacity-30 rounded-[8px] min-h-[100px]' : ''}`}>
                        <div className="grid gap-[4px] content-start h-full">
                            {tablesWithoutFolders.map((table) => renderTableItem(table, true, false, sectionName))}
                        </div>
                    </div>

                </div>
            </NavSection>
        );
    };

    return (
        <>
            {/* const renderTableSection = (tables, sectionName, showCreateNew = false, showAddFolder = true, folderPermission = null) => { */}
            {!isGuestUser && renderTableSection(privateTables, 'Private', true, true, "private")}
            {renderTableSection(sharedTables, 'Shared', false, true, "shared")}
            {!isGuestUser && renderTableSection(teamTables, 'Team', false, true, "team")}
        </>
    );
};

export default TableNav;